import classNames from 'classnames';
import React, { FC, memo } from 'react';
import SizeCodeDefaultImage from 'shared/assets/images/SizeCodeDefaultImage.png';
import { useAppTranslation } from 'app/config/i18Config/hooks';
import { getLocalizedString } from 'shared/utils/helpers/JSONLocalization';
import { SizeCodeForBooking, SizeCodeType } from 'entities/SizeCode';
import { RentOptionDocument } from 'entities/RentOption';
import { getGlobalSettings, useGetCurrencySymbol } from 'app/appState';
import { useAppSelector } from 'app/config/storeConfig/hooks';
import { getBoxDiscountedPriceAmount, getBoxPriceAmountWithVat } from 'entities/Box';
import { getWarehouseInfo } from 'pages/BookingPage';
import { Trans } from 'react-i18next';
import { getLoggedUserData } from 'entities/User';
import { Button } from 'shared/ui/Button';
import { formatPrice } from 'shared/utils/helpers/formatPrice';
import { formatDecimal } from 'shared/utils/helpers/formatDecimal';

interface SizeCodeCardProps {
  sizeCode: SizeCodeForBooking;
  rentOptions: RentOptionDocument[];
  isSelected: boolean;
  onClick: () => void;
}

export const SizeCodeCard: FC<SizeCodeCardProps> = memo((props) => {
  const { sizeCode, isSelected, onClick } = props;
  const { square, availableBoxesCount, imageUrl, description, sizeCodeType, minBoxPrice } = sizeCode;

  const { t } = useAppTranslation(['booking', 'common']);
  const currencySymbol = useGetCurrencySymbol();
  const warehouseInfo = useAppSelector(getWarehouseInfo);
  const globalSettings = useAppSelector(getGlobalSettings);
  const loggedUser = useAppSelector(getLoggedUserData);

  const noAvailableBoxes = availableBoxesCount === 0;

  const discountedPrice = getBoxDiscountedPriceAmount({
    warehouseDiscounts: warehouseInfo?.warehouseDiscounts,
    boxPrice: minBoxPrice,
  });

  const discountedBoxPriceWithVat = getBoxPriceAmountWithVat(discountedPrice, warehouseInfo, loggedUser?.accountType);
  const discountedBoxPrice = globalSettings?.showPriceWithVat ? discountedBoxPriceWithVat : discountedPrice;

  const minBoxPriceToDisplay = globalSettings?.showPriceWithVat
    ? getBoxPriceAmountWithVat(minBoxPrice, warehouseInfo, loggedUser?.accountType)
    : minBoxPrice;

  return (
    <div
      className={classNames('flex min-h-[110px] items-center p-2 rounded-lg cursor-pointer relative', {
        'cursor-default': noAvailableBoxes,
        'border-2 border-accent bg-green-50': isSelected,
        'hover:outline hover:outline-accent hover:outline-2': !isSelected && !noAvailableBoxes,
        'border-1.5 border-grey': !isSelected && noAvailableBoxes,
        'opacity-50': noAvailableBoxes,
      })}
      onClick={!noAvailableBoxes ? onClick : undefined}
    >
      <div className="shrink-0 mr-4">
        <img className="w-24 h-24 object-contain" src={imageUrl || SizeCodeDefaultImage} alt="Size image" />
      </div>

      <div className="flex-1 min-w-0 mr-8">
        <div className="text-24 font-bold text-primary mb-1 font-tektur">
          {formatDecimal(square)} м<sup className="text-base font-tektur font-bold">{sizeCodeType === SizeCodeType.CUBIC_BOX ? 3 : 2}</sup>
        </div>
        {description && <div className="text-sm text-primaryLight" dangerouslySetInnerHTML={{ __html: getLocalizedString(description) }} />}
      </div>

      <div className="text-right mr-8 mt-auto">
        <div className="text-md font-medium text-accent mb-1">
          {noAvailableBoxes ? t('No available boxes') : t('Boxes available: {{availableBoxesCount}}', { availableBoxesCount })}
        </div>
        {Boolean(minBoxPriceToDisplay) && (
          <div className="text-lg font-bold text-primary font-tektur">
            <Trans
              t={t}
              i18nKey={
                discountedBoxPrice ? 'From {{discountedPrice}} {{currencySymbol}}/month' : 'From {{minPrice}} {{currencySymbol}}/month'
              }
              components={[<span key="0" className="text-lg" />, <span key="1" className="font-semibold" />]}
              values={{
                minPrice: formatPrice(minBoxPriceToDisplay),
                currencySymbol,
                discountedPrice: formatPrice(discountedBoxPrice),
              }}
            />
          </div>
        )}
      </div>

      <div className="mt-auto">
        <Button theme="primary" size="medium" isDisabled={noAvailableBoxes} onClick={onClick}>
          {t('Select')}
        </Button>
      </div>
    </div>
  );
});
